<template>
  <ModalPlayerLayout class="transcription-modal" :closeModal="closeModal">
    <div class="video-transcript-container">
      <div id="video-transcript-header">
        <div class="video-transcript-search">
          <input
            type="text"
            v-model="inputSearch"
            :placeholder="$t('AllTraslation.searchTranscript')"
          />
          
          <div class="custom-select">
            <div class="selected-option" @click="toggleDropdown">
              {{ transcriptLang.toUpperCase() }}
            </div>
            <ul v-show="isDropdownOpen" class="options">
              <li
                v-for="language in languages"
                :key="language"
                @click="selectLanguage(language)"
              >
                {{ language.toUpperCase() }}
              </li>
            </ul>
          </div>
          <button
             v-if="getSkinInfos?.action_download_transcript"

            @click="downloadTranscript"
            class="video-transcript-download-btn"
            v-html="SVG.DOWNLOAD_CC"
            :title="$t('buttons.downloadTranscript')"

          ></button>
        </div>
      </div>
      <div
        class="transcript-list-container"
        ref="transcriptContainer"
      >
      <DynamicScroller
        class="transcript-list-list"
        :items="subtitles"
        :min-item-size="30"
        key-field="index"
      >
        <template v-slot="{ item, index, active }">
          <DynamicScrollerItem
            :item="item"
            :active="active"
            :size-dependencies="[item.content]"
          >
            <li
              role="option"
              class="transcript-list-item"
              :id="'transcript-cue-' + index"
              :class="item.startTime <= currentTime && item.endTime > currentTime ? 'is-current' : ''"
              :ref="item.startTime <= currentTime && item.endTime > currentTime ? 'currentItem' : ''"
              :tabindex="index"
              @click="navigateToTime(item.startTime)"
            >
              <span class="transcript-list-item-text" dir="ltr">
                {{ item.content }}
              </span>
              <span class="transcript-list-item-timestamp">
                {{ convertSecondsToTime(item.startTime) }}
              </span>
            </li>
          </DynamicScrollerItem>
        </template>
      </DynamicScroller>
      </div>
    </div>
  </ModalPlayerLayout>
</template>

<script>
import en from "../../../i18n/en_US";
import fr from "../../../i18n/fr_FR";
import ModalPlayerLayout from "../../layout/ModalPlayerLayout/ModalPlayerLayout.vue";
import { defineComponent } from "vue";
import { SVG } from "../../../components/utils/const";
import { mapGetters } from "vuex";
import { DynamicScroller, DynamicScrollerItem } from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'

export default defineComponent({
  name: "video-transcription",
  i18n: {
    messages: {
      en: en,
      fr: fr,
    },
  },
  components: {
    ModalPlayerLayout,
    DynamicScroller,
    DynamicScrollerItem
  },
  props: {
    information: {
      type: Object,
      required: true,
    },
    player: {
      type: Object,
      required: true,
    },
    closeModal: {
      type: Function,
      required: true,
    },
    currentTime: {
      type: Number,
      required: true,
    },
  },
  mounted() {
    this.languages = this.information?.playerOptions?.textTracks?.map(
      (lang) => lang.srclang
    );
    this.currentTranscript = this.information?.playerOptions?.textTracks.find(
      (lang) => lang.default
    );

    this.transcriptLang = this.currentTranscript.srclang;
    this.scrollCurrentItemIntoView();
  },
  data() {
    return {
      inputSearch: "",
      transcriptLang: "",
      SVG: SVG,
      languages: [],
      isDropdownOpen: false,
      currentTranscript: {},
      subtitles: [],
      originalSubtitles: [],
    };
  },
  methods: {
    
    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen;
    },
    selectLanguage(language) {
      this.transcriptLang = language;
      this.isDropdownOpen = false;
      this.currentTranscript = this.information?.playerOptions?.textTracks.find(
        (lang) => lang.srclang === language
      );
    },
    async downloadTranscript() {
      if (!this.currentTranscript?.src) {
        console.error("Aucun fichier de transcription disponible.");
        return;
      }

      try {
        const response = await fetch(this.currentTranscript.src);
        if (!response.ok) throw new Error("Erreur de téléchargement");

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        
        const downloadSubsLink = document.createElement("a");
        downloadSubsLink.href = url;
        downloadSubsLink.download = `${this.information?.playerOptions?.videoId}_${this.transcriptLang}.vtt`;
        document.body.appendChild(downloadSubsLink);
        downloadSubsLink.click();
        document.body.removeChild(downloadSubsLink);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Erreur lors du téléchargement :", error);
      }
    },
    async getSubtitles(vttFileUrl) {
    try {
        const response = await fetch(vttFileUrl);
        const blob = await response.blob();

        const textDecoder = new TextDecoder("utf-8");
        let vttData = textDecoder.decode(await blob.arrayBuffer());

        vttData = vttData.replace(/\r\n?/g, "\n");
        let lines = vttData.split("\n").filter(line => line.trim() !== "");

        if (lines[0].includes("WEBVTT")) {
            lines.shift();
        }

        let captionsArray = [];
        let i = 0;

        while (i < lines.length) {
            let index = lines[i].trim();
            let timestampLine = lines[i + 1];

            if (!/-->/g.test(timestampLine)) {
                i++;
                continue;
            }

            let timestampParts = timestampLine.split('-->').map(part => part.trim());
            let startTimeRaw = timestampParts[0].split(' ')[0];
            let endTimeRaw = timestampParts[1].split(' ')[0];

            i += 2;
            let contentLines = [];

            while (i < lines.length && !/^\d+$/.test(lines[i])) {
                contentLines.push(lines[i].trim());
                i++;
            }

            const captionObject = {
                index,
                startTime: this.parseTimestamp(startTimeRaw),
                endTime: this.parseTimestamp(endTimeRaw),
                content: contentLines.join(" "),
            };

            captionsArray.push(captionObject);
        }

        return captionsArray;
    } catch (error) {
        console.error("Error fetching or parsing VTT file:", error);
        return null;
    }
},

parseTimestamp(timestamp) {
    const colonCount = (timestamp.match(/:/g) || []).length;
    let parts;
    let seconds = 0;

    if (colonCount === 2) {
        parts = timestamp.split(':');
        seconds += parseInt(parts[0]) * 3600;
        seconds += parseInt(parts[1]) * 60;
        seconds += parseFloat(parts[2]);
    } else {
        parts = timestamp.split(':');
        seconds += parseInt(parts[0]) * 60;
        seconds += parseFloat(parts[1]);
    }

    return seconds;
},

    listTranscriptSubtitles(vttFileUrl) {
      this.getSubtitles(vttFileUrl)
        .then((subtitles) => {
          this.subtitles = subtitles;
          this.originalSubtitles = subtitles;
        })
        .catch((error) => {
          console.error(error);
        });
    },
    durationToSeconds(duration) {
      try {
        let res = duration.split(":");
        if (res.length === 3) {
          let seconds = parseInt(res[2]);
          let milliseconds = 0;
          if (res[2].includes(".")) {
            const [sec, ms] = res[2].split(".");
            seconds = parseInt(sec);
            milliseconds = parseInt(ms);
          } else if (res[2].includes(",")) {
            const [sec, ms] = res[2].split(",");
            seconds = parseInt(sec);
            milliseconds = parseInt(ms);
          }
          return (
            seconds +
            milliseconds / 1000 +
            parseInt(res[1]) * 60 +
            parseInt(res[0]) * 3600
          );
        } else if (res.length === 2) {
          return parseInt(res[1]) + parseInt(res[0]) * 60;
        }
        return -1;
      } catch (error) {
        return -1;
      }
    },
    convertSecondsToTime(seconds) {
      const totalSeconds = Math.floor(seconds);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const remainingSeconds = totalSeconds % 60;

      const formattedTime = `${
        hours > 0 ? (hours < 10 ? "0" : "") + hours + ":" : ""
      }${(minutes < 10 ? "0" : "") + minutes}:${
        (remainingSeconds < 10 ? "0" : "") + remainingSeconds
      }`;

      return formattedTime;
    },
    navigateToTime(time) {
      this.player.currentTime(time);
    },
    scrollCurrentItemIntoView() {
      const container = this.$refs.transcriptContainer;
      const currentItem = this.$refs.currentItem;
      if (
        container &&
        currentItem &&
        currentItem.length
      ) {
        currentItem[0].scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    },
  },
    computed: {
    ...mapGetters(["getSkinInfos"]),

  },
  watch: {
    currentTranscript(newVal) {
      this.listTranscriptSubtitles(newVal.src);
    },
    inputSearch(newVal) {
      this.subtitles = this.originalSubtitles.filter((caption) =>
        caption.content.toLowerCase().includes(newVal.toLowerCase())
      );
    },
    currentTime() {
      this.scrollCurrentItemIntoView();
    },
  },
});
</script>

<style>
@import "./TranscriptModal.css";
</style>
